import * as React from 'react'
import { AlertTriangle, RotateCcw } from 'lucide-react'

import { Button } from '@/components/ui/button'

interface Props {
  children: React.ReactNode
  /** Changing this resets the boundary — used to recover on navigation. */
  resetKey?: string
}

interface State {
  error: Error | null
}

/**
 * Catches a render error so one broken component does not blank the app.
 *
 * React unmounts the whole tree when a render throws and nothing catches it.
 * The symptom is a white page: no message, no way back, and whatever the person
 * was in the middle of writing is gone. That is what a `null.length` in the tag
 * rule preview did — and "命中 0" is the ordinary state of a rule being written,
 * so it happened at exactly the wrong moment.
 *
 * Placed around the routed page rather than the whole app, so the navigation
 * survives and the person can leave the broken page by clicking something.
 */
export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidUpdate(prev: Props) {
    // A new route is a new attempt; holding the old error would make every
    // other page look broken too.
    if (this.state.error && prev.resetKey !== this.props.resetKey) {
      this.setState({ error: null })
    }
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Kept in the console rather than sent anywhere: this is a development
    // build, and the stack is what makes the failure fixable.
    console.error('页面渲染出错', error, info.componentStack)
  }

  render() {
    if (!this.state.error) return this.props.children
    return (
      <div className="mx-auto max-w-lg py-16">
        <div className="rounded-lg border border-destructive/30 bg-destructive/[0.04] p-6">
          <div className="flex items-start gap-3">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-destructive" aria-hidden />
            <div className="min-w-0">
              <h2 className="text-sm font-medium">这个页面出错了</h2>
              <p className="mt-1 text-xs text-muted-foreground">
                页面没能渲染出来。左侧导航仍然可用，可以切换到别的页面。
              </p>
              <pre className="mt-3 max-h-40 overflow-auto rounded bg-muted/60 p-2 text-[11px] leading-5">
                {this.state.error.message || String(this.state.error)}
              </pre>
              <Button
                variant="outline"
                size="sm"
                className="mt-3"
                onClick={() => this.setState({ error: null })}
              >
                <RotateCcw className="mr-1 h-3.5 w-3.5" />重试
              </Button>
            </div>
          </div>
        </div>
      </div>
    )
  }
}
